/**
 * ATL-970 — the error a Supabase read helper throws when the fetch itself failed.
 *
 * It exists so that a caller can tell "this organization has no products" from "we could not find
 * out whether it has products". Before this, both arrived as `{}`.
 *
 * `collection` is the same value the helpers put on the Sentry `collection` tag, so a failure seen
 * in the app and a failure seen in Sentry are named identically.
 */
export class SupabaseFetchError extends Error {
  readonly collection: string;
  readonly statusCode?: number;
  // `Error.cause` is only declared under lib ES2022; this project targets lower, so declare it.
  readonly cause?: unknown;

  constructor(collection: string, cause?: unknown) {
    const message = (cause as { message?: string })?.message ?? String(cause);
    super(`Failed to fetch ${collection} from Supabase: ${message}`);
    this.name = 'SupabaseFetchError';
    this.collection = collection;
    // Assigned explicitly rather than through `super(msg, { cause })`: Hermes and the React Native
    // Babel preset do not implement the ES2022 `cause` option, so the second argument is silently
    // dropped there and the original error would be lost on device but not in tests.
    this.cause = cause;
    this.statusCode =
      (cause as { response?: { status?: number } })?.response?.status ??
      (cause as { status?: number })?.status;
    // Extending a built-in through Babel's ES5 output breaks the prototype chain, so
    // `instanceof SupabaseFetchError` would be false. The migration relies on that check.
    Object.setPrototypeOf(this, SupabaseFetchError.prototype);
  }
}

export default SupabaseFetchError;
